import { useEffect, useState } from 'react';

interface PageVisibilityState {
  isVisible: boolean;
  visibilityState: DocumentVisibilityState;
  isBackground: boolean;
  timeBecameVisible: number | null;
  timeBecameHidden: number | null;
}

/**
 * Hook to detect when the app goes to background/foreground
 * Useful for pausing expensive operations when app is not visible
 */
export function usePageVisibility(): PageVisibilityState {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [visibilityState, setVisibilityState] =
    useState<DocumentVisibilityState>(document.visibilityState);
  const [timeBecameVisible, setTimeBecameVisible] = useState<number | null>(
    null,
  );
  const [timeBecameHidden, setTimeBecameHidden] = useState<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      const newIsVisible = !document.hidden;
      const newVisibilityState = document.visibilityState;

      // Only proceed if visibility actually changed
      if (newIsVisible === isVisible) {
        return;
      }

      setIsVisible(newIsVisible);
      setVisibilityState(newVisibilityState);

      if (newIsVisible) {
        // App just became visible (resumed from background)
        setTimeBecameVisible(now);
        console.log('[PageVisibility] App resumed from background');
      } else {
        // App just became hidden (went to background)
        setTimeBecameHidden(now);
        console.log('[PageVisibility] App went to background');
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible]);

  return {
    isVisible,
    visibilityState,
    isBackground: !isVisible,
    timeBecameVisible,
    timeBecameHidden,
  };
}
